// useDelegatedCapabilities — the capabilities someone has DELEGATED to the
// signed-in user right now (DEF-0mvpyq10d005, NA-3).
//
// Grace covering David's leave approvals is an employee: her role carries no
// leave:approve, so the role-based nav and routes gave her no way to the queue
// she is covering. This asks auth for the delegations in force for her
// (GET /rbac/delegations/active-for-me). It only OPENS an entry — what she sees
// in it is the backend's: leave-service lists only the delegated people.
//
// Fails closed: an error (or no user) is "nothing delegated", never "all".
// One answer per user, briefly cached, so the sidebar and the route share it,
// and kept current while they are mounted (see below).
import { useEffect, useState } from 'react';
import { useAppSelector } from '@/app/hooks';
import { getActiveDelegationsForMe } from '@/services/rbacService';

const TTL_MS = 60_000;

// One answer per user, shared by every mounted consumer (the sidebar and the
// route). ⚠️ STAYS CURRENT WHILE MOUNTED (web #62 approval): the TTL used to be
// consulted only when a consumer loaded, which it did once per user — after a
// revoke or expiry the nav item and the route stayed until a reload, and a
// delegation granted mid-session stayed hidden. Now a mounted consumer
// re-asks once the answer is older than the TTL (a timer, and on returning to
// the tab, where timers are throttled), a 403 from the delegated work
// invalidates at once (invalidateDelegatedCapabilities), and every new answer
// reaches every mounted consumer.
type Entry = { at: number; caps: string[] | null; pending: Promise<string[]> | null };
const cache = new Map<string, Entry>();
const listeners = new Map<string, Set<(caps: string[]) => void>>();
// The next refresh per user, timed from the cached answer's EXPIRY (its
// arrival + TTL), not from when a consumer mounted (web #65 approval): an
// interval started at mount met a 59 s-old answer at 60 s (with 1 s of
// latency) and re-asked only at 120 s, so a revoke could last two TTLs.
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function scheduleRefresh(userId: string): void {
  const prev = timers.get(userId);
  if (prev) clearTimeout(prev);
  timers.delete(userId);
  const e = cache.get(userId);
  if (!listeners.get(userId)?.size || !e?.caps) return; // nobody mounted, or nothing answered yet
  const delay = Math.max(0, e.at + TTL_MS - Date.now());
  timers.set(userId, setTimeout(() => {
    timers.delete(userId);
    void load(userId, true);
  }, delay));
}

function notify(userId: string, caps: string[]): void {
  listeners.get(userId)?.forEach((fn) => fn(caps));
}

function load(userId: string, force = false): Promise<string[]> {
  const hit = cache.get(userId);
  if (hit?.pending) return hit.pending;                   // one request at a time per user
  if (!force && hit?.caps && Date.now() - hit.at < TTL_MS) return Promise.resolve(hit.caps);
  const pending = getActiveDelegationsForMe()
    .then((rows) => [...new Set(rows.flatMap((r) => r.capabilities ?? []))])
    .catch(() => [] as string[])                          // fails closed
    .then((caps) => {
      cache.set(userId, { at: Date.now(), caps, pending: null });
      scheduleRefresh(userId);
      notify(userId, caps);
      return caps;
    });
  cache.set(userId, { at: hit?.at ?? 0, caps: hit?.caps ?? null, pending });
  return pending;
}

/** Test seam: forget every cached answer. */
export function resetDelegatedCapabilitiesCache(): void {
  cache.clear();
  timers.forEach((t) => clearTimeout(t));
  timers.clear();
}

/**
 * Re-ask auth now for every signed-in user's delegations (a delegated page got
 * a 403: the delegation was revoked or has expired). Mounted consumers update
 * when the answer arrives; nothing is shown from the old answer as "current"
 * longer than that.
 */
export function invalidateDelegatedCapabilities(): void {
  for (const userId of listeners.keys()) void load(userId, true);
  for (const [userId, e] of cache) if (!listeners.has(userId)) cache.set(userId, { ...e, at: 0 });
}

export function useDelegatedCapabilities(): { capabilities: string[]; ready: boolean } {
  const userId = useAppSelector((s) => s.auth?.user?.id) ?? null;
  const [state, setState] = useState<{ userId: string | null; capabilities: string[]; ready: boolean }>({
    userId: null,
    capabilities: [],
    ready: false,
  });
  useEffect(() => {
    if (!userId) {
      setState({ userId: null, capabilities: [], ready: true });
      return;
    }
    let live = true;
    const apply = (capabilities: string[]) => {
      if (live) setState({ userId, capabilities, ready: true });
    };
    const set = listeners.get(userId) ?? new Set();
    set.add(apply);
    listeners.set(userId, set);
    void load(userId).then(apply);
    // Revalidate while mounted: when the answer expires (scheduleRefresh, armed
    // by every answer), and on returning to the tab (timers are throttled there).
    scheduleRefresh(userId);
    const onVisible = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') void load(userId);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      live = false;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      set.delete(apply);
      if (set.size === 0) {
        listeners.delete(userId);
        scheduleRefresh(userId); // nobody left: clears the timer
      }
    };
  }, [userId]);
  // Another user's answer is never shown, even for a render.
  if (state.userId !== userId) return { capabilities: [], ready: !userId };
  return { capabilities: state.capabilities, ready: state.ready };
}
